/**
 * @file HeroAlertsCta.jsx
 * @description Hero call to action for public pages (one engine, no copies).
 *   HeroCtaButton: the phone-first primary button (at least 48px tall, full width on phones, so it sits on the
 *   first screen). HeroAlertsCta: the provider "turn on case alerts" action on top of it (tracks the click, sets
 *   the "alerts" sign-up intent, hands off to auth).
 *   Used by: jobs-by-state, the roles and compare guides, the ABA Case Finder landing (phones, alerts) and the
 *   For Agencies landing (phones, post a case, via HeroCtaButton).
 * @module components/HeroAlertsCta
 */
import { useTranslation } from "react-i18next";
import { BellIcon } from "./Icons";
import { T } from "../theme";
import { setSignupIntent } from "../lib/signupIntent";
import { track, A, ctaMeta } from "../lib/analytics";

/** Primary hero button. `icon` is an optional element shown before the label. */
export function HeroCtaButton({ label, onClick, icon = null, isMobile, testId }) {
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: isMobile ? "center" : "flex-start" }}>
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
          width: isMobile ? "100%" : "auto", minHeight: 48, padding: isMobile ? "13px 24px" : "15px 30px",
          background: T.gradientIndigo, color: "#fff", border: "none", borderRadius: 12,
          fontSize: 15, fontWeight: 800, cursor: "pointer", fontFamily: T.fontBody,
          boxShadow: "0 6px 24px rgba(79,70,229,0.4)",
        }}
      >
        {icon} {label}
      </button>
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} props.label Button text (already translated).
 * @param {string} props.pagePath Page path with trailing slash, for analytics (e.g. "/aba-roles-guide/").
 * @param {string} [props.pageType] Analytics page type (default "resource").
 * @param {string} [props.audience] Analytics audience (default "resource").
 * @param {string} [props.ctaLabel] Stable English label for analytics (default "Join ABA case alerts").
 * @param {(view: string) => void} props.onNavigate Page navigation (the same one the page already has).
 * @param {boolean} props.isMobile Phone layout: full width.
 */
export default function HeroAlertsCta({ label, pagePath, pageType = "resource", audience = "resource", ctaLabel = "Join ABA case alerts", onNavigate, isMobile }) {
  const { i18n } = useTranslation();
  const go = () => {
    track(A.RESOURCE_PAGE_CTA_CLICK, {
      supabaseMeta: { page: pagePath },
      ga4Params: ctaMeta({ pagePath, pageType, ctaLabel, targetPath: "/auth", userLanguage: i18n.language, audience }),
    });
    setSignupIntent("alerts");
    onNavigate("auth");
  };
  return <HeroCtaButton testId="cta-alerts" label={label} onClick={go} icon={<BellIcon size={16} color="#fff" />} isMobile={isMobile} />;
}
